import { get, post } from "lib/api";

// Toggle this to enable/disable debug logs for purchase receipt generation
export let PURCHASE_RECEIPTS_DEBUG = true;
export const setPurchaseReceiptsDebug = (value) => { PURCHASE_RECEIPTS_DEBUG = !!value; };
const dlog = (...args) => { if (PURCHASE_RECEIPTS_DEBUG) console.log("[purchaseReceipts]", ...args); };

const normalizeEntity = (raw) => {
  if (!raw) return null;
  if (raw.data) return normalizeEntity(raw.data);
  if (raw.attributes) return { id: raw.id, documentId: raw.documentId || raw.id, ...raw.attributes };
  return { ...raw, documentId: raw.documentId || raw.id };
};

async function generateTicketNumber() {
  const prefix = "REC";
  const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
  const startsWith = `${prefix}-${dateStr}`;
  dlog("generateTicketNumber: prefix", prefix, "dateStr", dateStr);
  
  // Query for ALL receipts (including drafts/unpublished) to avoid duplicates
  const resp = await get("/api/purchase-receipts", {
    filters: { ticketNumber: { $startsWith: startsWith } },
    sort: "ticketNumber:desc",
    pagination: { page: 1, pageSize: 100 },
    fields: ["ticketNumber"],
    publicationState: "preview", // Get both published and draft receipts
  });
  
  // Handle different Strapi response formats
  let rawData = resp?.data;
  if (Array.isArray(rawData)) {
    // Direct array response
  } else if (rawData?.data && Array.isArray(rawData.data)) {
    // Wrapped in data property
    rawData = rawData.data;
  } else if (rawData?.results && Array.isArray(rawData.results)) {
    // Wrapped in results property
    rawData = rawData.results;
  } else {
    rawData = [];
  }
  
  const existing = rawData.map((d) => {
    if (d.attributes) {
      return d.attributes.ticketNumber || d.ticketNumber;
    }
    return d.ticketNumber;
  }).filter(Boolean);
  
  dlog("existing purchase receipts today:", existing, "raw response:", resp);
  let maxNum = 0;
  existing.forEach((pr) => {
    const m = String(pr).match(/-(\d+)$/);
    if (m) {
      const num = parseInt(m[1], 10);
      if (!Number.isNaN(num) && num > maxNum) maxNum = num;
    }
  });
  const next = String(maxNum + 1).padStart(3, "0");
  const number = `${startsWith}-${next}`;
  dlog("next ticketNumber:", number);
  return number;
}

export async function generatePurchaseReceiptClientSide(purchaseOrderIdOrDocId) {
  dlog("generatePurchaseReceiptClientSide called with:", purchaseOrderIdOrDocId);
  
  // Load purchase order with items + inventory
  let poRaw = null;
  try {
    const poResp = await get(`/api/purchase-orders/${purchaseOrderIdOrDocId}`, {
      populate: {
        vendor: true,
        items: {
          populate: {
            inventoryItem: true,
          },
        },
      },
    });
    dlog("direct GET full response:", JSON.stringify(poResp, null, 2));
    // Try multiple response shapes
    poRaw = poResp?.data?.data || poResp?.data || poResp || null;
    // If it's wrapped in attributes, unwrap it
    if (poRaw?.attributes) {
      poRaw = { id: poRaw.id, documentId: poRaw.documentId || poRaw.id, ...poRaw.attributes };
    }
    dlog("direct GET purchase-order result present:", !!poRaw, poRaw ? { id: poRaw.id, documentId: poRaw.documentId } : null);
  } catch (e) {
    dlog("direct GET purchase-order failed:", e?.message);
    poRaw = null;
  }

  if (!poRaw) {
    dlog("Failed to load purchase order");
    return { ok: false, error: "Failed to load purchase order" };
  }

  const po = normalizeEntity(poRaw);
  dlog("normalized purchase order:", { id: po.id, documentId: po.documentId, itemsCount: (po.items || []).length });

  if (!po.items || po.items.length === 0) {
    return { ok: false, error: "Purchase order has no items" };
  }

  // Normalize items
  const rawItems = po.items?.data || po.items || [];
  const normalizedItems = rawItems.map((item) => {
    let normalizedItem;
    if (item.attributes) {
      normalizedItem = { id: item.id, documentId: item.documentId || item.id, ...item.attributes };
    } else {
      normalizedItem = { ...item, documentId: item.documentId || item.id };
    }
    
    // Normalize inventoryItem
    if (normalizedItem.inventoryItem) {
      if (typeof normalizedItem.inventoryItem === 'number' || typeof normalizedItem.inventoryItem === 'string') {
        // Just an ID
      } else if (normalizedItem.inventoryItem.data) {
        if (normalizedItem.inventoryItem.data.attributes) {
          normalizedItem.inventoryItem = { id: normalizedItem.inventoryItem.data.id, ...normalizedItem.inventoryItem.data.attributes };
        } else {
          normalizedItem.inventoryItem = normalizedItem.inventoryItem.data;
        }
      } else if (normalizedItem.inventoryItem.attributes) {
        normalizedItem.inventoryItem = { id: normalizedItem.inventoryItem.id, ...normalizedItem.inventoryItem.attributes };
      }
    }
    
    return normalizedItem;
  });

  dlog("normalized items:", normalizedItems.length);

  // Generate ticket number
  let ticketNumber = await generateTicketNumber();
  const maxRetries = 5;
  let retryCount = 0;

  // Helper function to check if a ticket number already exists
  const checkTicketNumberExists = async (ticketNum) => {
    try {
      const checkResp = await get("/api/purchase-receipts", {
        filters: { ticketNumber: { $eq: ticketNum } },
        publicationState: "preview", // Check both published and draft
        pagination: { pageSize: 1 },
      });
      const results = checkResp?.data || checkResp?.data?.data || [];
      const exists = results.length > 0;
      dlog(`Ticket number ${ticketNum} exists:`, exists);
      return exists;
    } catch (e) {
      dlog("Error checking ticket number:", e);
      return false; // If check fails, assume it doesn't exist and try to create
    }
  };

  while (retryCount < maxRetries) {
    dlog("creating purchase receipt with ticket number:", ticketNumber, "and purchaseOrder:", po.documentId || po.id, `(attempt ${retryCount + 1}/${maxRetries})`);

    // Check if ticket number already exists before trying to create
    const alreadyExists = await checkTicketNumberExists(ticketNumber);
    if (alreadyExists) {
      dlog(`Ticket number ${ticketNumber} already exists, generating new one...`);
      await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
      ticketNumber = await generateTicketNumber();
      retryCount++;
      continue;
    }

    try {
      // Create purchase receipt
      const createResp = await post("/api/purchase-receipts", {
        data: {
          ticketNumber,
          purchaseOrder: po.documentId || po.id,
          status: "draft",
        },
      });

      dlog("create response:", JSON.stringify(createResp, null, 2));
      
      const created = createResp?.data?.data || createResp?.data;
      dlog("create response present:", !!created);
      
      // Check for validation errors in response
      if (createResp?.error || (createResp?.data?.error)) {
        const error = createResp?.error || createResp?.data?.error;
        dlog("Error in response:", JSON.stringify(error, null, 2));
        
        const errorMessage = error?.message || error?.details?.errors?.[0]?.message || JSON.stringify(error);
        
        // Check for uniqueness error - handle multiple error formats
        const isUniquenessError = (
          errorMessage?.toLowerCase().includes("unique") ||
          errorMessage?.toLowerCase().includes("must be unique") ||
          (error?.name === "ValidationError" && 
           error?.details?.errors?.some((e) => 
             (e.path?.includes("ticketNumber") || e.path?.includes("ticket")) && 
             (e.message?.includes("unique") || e.message?.includes("must be unique"))
           ))
        );
        
        dlog("Is uniqueness error?", isUniquenessError, "Error message:", errorMessage);
        
        if (isUniquenessError && retryCount < maxRetries - 1) {
          dlog("Uniqueness error detected, generating new ticketNumber and retrying...");
          // Wait a small random amount to reduce race conditions
          await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
          ticketNumber = await generateTicketNumber();
          retryCount++;
          continue;
        } else {
          dlog("Failed to create purchase receipt:", errorMessage);
          return { ok: false, error: errorMessage };
        }
      }

      if (!created) {
        dlog("No created entity in response, full response:", JSON.stringify(createResp, null, 2));
        return { ok: false, error: "Failed to create purchase receipt: no data returned" };
      }

      const pr = created.attributes
        ? { id: created.id, documentId: created.documentId || created.id, ...created.attributes }
        : { ...created, documentId: created.documentId || created.id };
      dlog("created purchase receipt:", { id: pr.id, documentId: pr.documentId });
      return { ok: true, purchaseReceipt: pr };
    } catch (e) {
      dlog("Error creating purchase receipt:", e);
      dlog("Error response:", JSON.stringify(e?.response?.data, null, 2));
      dlog("Error status:", e?.response?.status);
      
      // Check if it's a validation error we can retry
      const errorData = e?.response?.data;
      const errorObj = errorData?.error || errorData;
      
      // Log full error structure for debugging
      dlog("Full error object:", JSON.stringify(errorObj, null, 2));
      
      // Check for uniqueness error - handle multiple error formats
      const errorMessage = errorObj?.message || errorObj?.details?.errors?.[0]?.message || errorData?.message || e?.message || "Failed to create purchase receipt";
      const isUniquenessError = (
        errorMessage?.toLowerCase().includes("unique") ||
        errorMessage?.toLowerCase().includes("must be unique") ||
        (errorObj?.name === "ValidationError" && 
         errorObj?.details?.errors?.some((err) => 
           (err.path?.includes("ticketNumber") || err.path?.includes("ticket")) && 
           (err.message?.includes("unique") || err.message?.includes("must be unique"))
         ))
      );
      
      dlog("Is uniqueness error?", isUniquenessError, "Error message:", errorMessage);
      
      if (isUniquenessError && retryCount < maxRetries - 1) {
        dlog("Uniqueness error detected in catch, generating new ticketNumber and retrying...");
        await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
        ticketNumber = await generateTicketNumber();
        retryCount++;
        continue;
      }
      
      // Don't retry non-uniqueness errors
      return { ok: false, error: errorMessage };
    }
  }

  return { ok: false, error: "Failed to create purchase receipt after retries" };
}

export async function findActivePurchaseReceiptForPo(purchaseOrderIdOrDocId) {
  if (!purchaseOrderIdOrDocId) return null;

  let po = null;
  try {
    const poResp = await get(`/api/purchase-orders/${purchaseOrderIdOrDocId}`, {
      fields: ["id", "documentId", "purchaseOrderNumber"],
    });
    const poRaw = poResp?.data?.data || poResp?.data || poResp || null;
    po = normalizeEntity(poRaw);
  } catch (e) {
    dlog("findActivePurchaseReceiptForPo: failed to load purchase order", e?.message);
  }

  const poDocId = po?.documentId || purchaseOrderIdOrDocId;
  if (!poDocId) return null;

  const resp = await get("/api/purchase-receipts", {
    publicationState: "preview",
    sort: "updatedAt:desc",
    pagination: { page: 1, pageSize: 1 },
    fields: ["ticketNumber", "status", "id", "documentId", "updatedAt", "receivedBy", "receivedAt"],
    populate: {
      purchaseOrder: {
        fields: ["id", "documentId", "purchaseOrderNumber"],
      },
    },
    filters: {
      status: { $in: ["draft", "in_review"] },
      purchaseOrder: { documentId: { $eq: String(poDocId) } },
    },
  });

  let raw = resp?.data;
  if (raw?.data && Array.isArray(raw.data)) raw = raw.data;
  if (!Array.isArray(raw)) raw = [];
  return raw.length > 0 ? normalizeEntity(raw[0]) : null;
}

export async function openOrCreatePurchaseReceiptForPo(purchaseOrderIdOrDocId) {
  const existing = await findActivePurchaseReceiptForPo(purchaseOrderIdOrDocId);
  if (existing) {
    return { ok: true, purchaseReceipt: existing, created: false };
  }
  return generatePurchaseReceiptClientSide(purchaseOrderIdOrDocId);
}

/**
 * Sum receivedQuantity from completed purchase receipts for a PO, keyed by purchase-order-item documentId (fallback id).
 * Excludes the given receipt documentId (e.g. current ticket) so defaults reflect prior shipments only.
 */
export async function fetchPriorReceivedQuantitiesByPoItem(poDocumentId, excludeReceiptDocumentId = null) {
  if (!poDocumentId) return {};
  const resp = await get("/api/purchase-receipts", {
    publicationState: "preview",
    filters: {
      $and: [
        { purchaseOrder: { documentId: { $eq: String(poDocumentId) } } },
        { status: { $eq: "completed" } },
      ],
    },
    populate: {
      lines: {
        populate: {
          purchaseOrderItem: { fields: ["documentId", "id"] },
        },
        fields: ["receivedQuantity"],
      },
    },
    pagination: { pageSize: 100 },
  });

  const rowsRaw = resp?.data || [];
  const rowsArray = Array.isArray(rowsRaw) ? rowsRaw : rowsRaw?.data || [];
  const rowsByDoc = new Map();
  for (const row of rowsArray) {
    const key = String(row?.documentId || row?.id || "");
    if (!key) continue;
    const prev = rowsByDoc.get(key);
    const prevScore = prev?.publishedAt ? 2 : prev?.id ? 1 : 0;
    const nextScore = row?.publishedAt ? 2 : row?.id ? 1 : 0;
    if (!prev || nextScore >= prevScore) rowsByDoc.set(key, row);
  }
  const map = {};

  const normalizeLine = (ln) => {
    if (!ln) return null;
    if (ln.attributes) {
      return { ...ln.attributes, documentId: ln.documentId || ln.id, id: ln.id };
    }
    return ln;
  };

  for (const row of rowsByDoc.values()) {
    const rid = row.documentId || row.id;
    if (excludeReceiptDocumentId != null && String(rid) === String(excludeReceiptDocumentId)) {
      continue;
    }
    const lines = row.lines?.data || row.lines || [];
    const linesByDoc = new Map();
    for (const ln of lines) {
      const normalized = normalizeLine(ln);
      const lineKey = normalized?.documentId != null ? String(normalized.documentId) : normalized?.id != null ? String(normalized.id) : null;
      if (!lineKey) continue;
      const prev = linesByDoc.get(lineKey);
      const prevScore = prev?.publishedAt ? 2 : prev?.id ? 1 : 0;
      const nextScore = normalized?.publishedAt ? 2 : normalized?.id ? 1 : 0;
      if (!prev || nextScore >= prevScore) linesByDoc.set(lineKey, normalized);
    }
    for (const line of linesByDoc.values()) {
      let poi = line.purchaseOrderItem;
      if (poi?.data) {
        const d = poi.data;
        poi = d.attributes ? { ...d.attributes, documentId: d.documentId, id: d.id } : d;
      }
      const key =
        poi?.documentId != null ? String(poi.documentId) : poi?.id != null ? String(poi.id) : null;
      if (!key) continue;
      const qty = parseFloat(line.receivedQuantity || 0) || 0;
      map[key] = (map[key] || 0) + qty;
    }
  }
  return map;
}

